import { reportUiFailure, reportUiKpi } from "../../report-failure.js";
import type { ChannelSlashCommand } from "../../components/composer/slash-commands.js";
import type { AgentSlashCommand } from "../../types.js";
import {
  defaultDmSessionForCybo,
  isMine,
  type CyboSessionFields,
  type ViewerIdentity,
} from "../../session-scope.js";

// CYBORG-1410: the composer turns a typed `/name args` into the typed cyborg:agent_command
// fields on the CLIENT. Catalog commands and known session controls qualify, so
// `/tmp/foo`, `/shrug` and an unknown name stay ordinary text. The server never parses a
// `/` out of prompt text; it validates these fields again.

export interface AgentCommandInput {
  name: string;
  args: string;
}

const TYPED_COMMAND_RE = /^\/([A-Za-z0-9][A-Za-z0-9_.:-]*)(?:\s+([\s\S]*))?$/;
const SESSION_CONTROLS = new Set(["clear", "compact", "goal", "context", "rewind", "usage"]);

// An empty catalog leaves validation to the server; an answered one proves lack of support.
export function unsupportedClearRefusal(
  text: string,
  catalog: ReadonlyArray<{ name: string }>,
): string | undefined {
  const match = TYPED_COMMAND_RE.exec(text.trim());
  if (!match || catalog.length === 0) return undefined;
  const name = match[1].toLowerCase();
  if (!SESSION_CONTROLS.has(name)) return undefined;
  if (catalog.some((c) => c.name.toLowerCase() === name)) return undefined;
  return `This agent doesn't support /${name}.`;
}

// `/login` and `/logout` never reach the provider: Cyborg signs the agent's provider in or out on
// the computer that runs it (provider-auth-chat.svelte.ts).
export function providerAuthCommandOf(text: string): "login" | "logout" | undefined {
  const typed = text.trim().toLowerCase();
  if (typed === "/login") return "login";
  if (typed === "/logout") return "logout";
  return undefined;
}

export function parseTypedAgentCommand(
  text: string,
  catalog: ReadonlyArray<{ name: string }>,
): AgentCommandInput | undefined {
  const match = TYPED_COMMAND_RE.exec(text.trim());
  if (!match) return undefined;
  const name = match[1];
  if (
    !catalog.some((c) => c.name === name) &&
    !(catalog.length === 0 && SESSION_CONTROLS.has(name.toLowerCase()))
  )
    return undefined;
  return { name: catalog.length === 0 ? name.toLowerCase() : name, args: (match[2] ?? "").trim() };
}

// Our own wire limit, not the provider's: CyborgAgentCommandSchema.args in the server's
// cyborg-messages.ts (AGENT_COMMAND_ARGS_MAX). The UI cannot import the server, so
// AgentComposer.long-goal-reload.lifecycle.test.ts fails if the two numbers drift.
export const AGENT_COMMAND_ARGS_MAX = 4000;

// Why this command cannot be sent, said plainly (never the relay's raw schema text), or
// undefined when it can. The composer keeps the draft and shows this beside it.
export function agentCommandRefusal(cmd: AgentCommandInput): string | undefined {
  if (cmd.args.length <= AGENT_COMMAND_ARGS_MAX) return undefined;
  const what = cmd.name.toLowerCase() === "goal" ? "goal" : "command";
  return `That ${what} is too long (${cmd.args.length} characters, max ${AGENT_COMMAND_ARGS_MAX}). Shorten it and send again.`;
}

// Put a reload's dropped queue text back after what the box already holds, never twice: a draft
// (this device's cache or one the server synced) that already carries a dropped message is the
// same text, so it is skipped. Restoring is idempotent however many reloads happen.
export function mergeRestoredText(draft: string, dropped: readonly string[]): string {
  // On paragraph boundaries, so a multi-paragraph message already in the draft is found whole too.
  const padded = `\n\n${draft.trim()}\n\n`;
  const fresh = dropped.filter((text) => text.trim() && !padded.includes(`\n\n${text.trim()}\n\n`));
  return [draft, ...fresh].filter(Boolean).join("\n\n");
}

export function agentCommandText(cmd: AgentCommandInput): string {
  return cmd.args ? `/${cmd.name} ${cmd.args}` : `/${cmd.name}`;
}

// The cybo's live default session behind a DM, when the viewer OWNS it. Undefined for a human
// peer (not a cybo), another member's session, or a record-only `dmdef-` default (nothing to run on).
export function ownLiveCyboSession<T extends CyboSessionFields>(
  peerId: string,
  cyboIds: readonly string[],
  sessions: readonly T[],
  viewer: ViewerIdentity,
): T | undefined {
  if (!cyboIds.includes(peerId)) return undefined;
  const session = defaultDmSessionForCybo(
    sessions.filter((s) => isMine(s, viewer)),
    peerId,
  );
  return session && !session.agentId.startsWith("dmdef-") ? session : undefined;
}

// The viewer's record-only `dmdef-` default behind a cybo DM: nothing to RUN a command on, but the
// daemon still lists the provider's catalog for it (the cybo record names the provider). That catalog
// is what lets the composer refuse a command the provider lacks (Codex /clear) instead of sending it.
export function recordOnlyCyboSession<T extends CyboSessionFields>(
  peerId: string,
  cyboIds: readonly string[],
  sessions: readonly T[],
  viewer: ViewerIdentity,
): T | undefined {
  if (!cyboIds.includes(peerId)) return undefined;
  const session = defaultDmSessionForCybo(
    sessions.filter((s) => isMine(s, viewer)),
    peerId,
  );
  return session?.agentId.startsWith("dmdef-") ? session : undefined;
}

// What a composer gets in a DM with the viewer's own cybo: the session's catalog, a lazy loader,
// and the send. MessageInput and RichMessageInput share everything below.
// The closed set the relay accepts for the palette KPI; a custom command's own name is the user's
// word and is never sent.
const BUILTIN_COMMAND_LABELS: ReadonlySet<string> = new Set([
  "goal",
  "compact",
  "clear",
  "context",
  "init",
  "loop",
  "schedule",
  "usage",
  "debug",
  "heapdump",
  "extra-usage",
  "login",
  "logout",
  "exit",
  "quit",
  "rewind",
  "model",
  "mode",
  "permissions",
  "thinking",
  "effort",
]);

export function commandKpiLabel(name: string): string {
  const lower = name.toLowerCase();
  return BUILTIN_COMMAND_LABELS.has(lower) ? lower : "custom";
}

// A typed command left a composer (CYBORG-1410 palette telemetry).
export function reportCommandUsed(
  surface: "dm" | "agent",
  name: string,
  workspaceId: string | undefined,
): void {
  reportUiKpi("commands.palette_used", {
    ...(workspaceId ? { workspaceId } : {}),
    surface,
    command: commandKpiLabel(name),
  });
}

// The typed command behind a prompt id, so a Retry or a requeue resends it AS the command and never
// as the prose that happens to read the same.
const typedByPromptId = new Map<string, AgentCommandInput>();
const TYPED_BY_PROMPT_MAX = 200;

export function rememberTypedCommand(promptId: string, cmd: AgentCommandInput): void {
  if (typedByPromptId.size >= TYPED_BY_PROMPT_MAX) {
    const oldest = typedByPromptId.keys().next().value;
    if (oldest !== undefined) typedByPromptId.delete(oldest);
  }
  typedByPromptId.set(promptId, cmd);
}

export function typedCommandOf(promptId: string | undefined): AgentCommandInput | undefined {
  return promptId ? typedByPromptId.get(promptId) : undefined;
}

export interface AgentCommandsHost {
  workspaceId?: string;
  commands: AgentSlashCommand[];
  ensureLoaded: () => void;
  // The catalog, fetched if it is not here yet (a pasted or restored "/goal x" never opened the menu).
  load: () => Promise<AgentSlashCommand[]>;
  run: (cmd: AgentCommandInput) => Promise<void>;
  providerAuth?: (kind: "login" | "logout") => void;
}

const CATALOG_WAIT_MS = 2_500;

// A catalog fetch, given a short time to answer; a slow one reads as "no commands".
export function catalogWithin(
  fetch: Promise<AgentSlashCommand[]>,
  ms: number = CATALOG_WAIT_MS,
): Promise<AgentSlashCommand[]> {
  return Promise.race([
    fetch,
    new Promise<AgentSlashCommand[]>((resolve) => setTimeout(() => resolve([]), ms)),
  ]);
}

// The '/' menu in a session context: the agent's commands win over a channel command of the same
// trigger, here and in submitTypedAgentCommand (which runs first), so the menu never offers one
// thing and runs another.
export function mergeSlashMenuItems(
  channel: readonly ChannelSlashCommand[],
  host: AgentCommandsHost | undefined,
  query: string,
): ChannelSlashCommand[] {
  if (!host) return [...channel];
  const q = query.toLowerCase();
  const agent: ChannelSlashCommand[] = host.commands
    .filter((c) => c.name.toLowerCase().startsWith(q))
    .map((c) => ({
      trigger: c.name,
      hint: c.argumentHint || undefined,
      description: c.description,
      dmSupported: true,
    }));
  const claimed = new Set(agent.map((c) => c.trigger.toLowerCase()));
  return [...channel.filter((c) => !claimed.has(c.trigger.toLowerCase())), ...agent];
}

export type TypedCommandSubmit =
  | { kind: "none" }
  | { kind: "ran" }
  | { kind: "error"; message: string };

// The composer's submit branch: a catalog `/name args` with no attachment or staged page runs as a
// typed command and is never posted as text. Anything else is left to the normal send.
export async function submitTypedAgentCommand(
  host: AgentCommandsHost | undefined,
  text: string,
  captioned: boolean,
): Promise<TypedCommandSubmit> {
  if (!host || captioned || !text.trimStart().startsWith("/")) return { kind: "none" };
  const auth = providerAuthCommandOf(text);
  if (auth && host.providerAuth) {
    host.providerAuth(auth);
    return { kind: "ran" };
  }
  let commands = host.commands;
  if (commands.length === 0) {
    try {
      commands = await catalogWithin(host.load());
    } catch (err) {
      reportUiFailure("composer.agent_command.catalog", err);
    }
  }
  const typed = parseTypedAgentCommand(text, commands);
  if (!typed) {
    const unsupported = unsupportedClearRefusal(text, commands);
    if (unsupported) {
      reportUiFailure("composer.agent_command", new Error(unsupported), {
        failure_kind: "refusal",
      });
      return { kind: "error", message: unsupported };
    }
    return { kind: "none" };
  }
  const refusal = agentCommandRefusal(typed);
  if (refusal) return { kind: "error", message: refusal };
  try {
    await host.run(typed);
    reportCommandUsed("dm", typed.name, host.workspaceId);
    return { kind: "ran" };
  } catch (err) {
    reportUiFailure("composer.agent_command", err);
    return {
      kind: "error",
      message: err instanceof Error && err.message ? err.message : "Couldn't run that command",
    };
  }
}

// An OLDER daemon refuses a command while the agent is mid-turn, before it writes anything. That is a
// "wait", never a "drop": the DM composer retries. A current daemon holds the command itself and
// never answers busy.
export function isBusyRefusal(err: unknown): boolean {
  return typeof err === "object" && err !== null && (err as { code?: unknown }).code === "busy";
}

const BUSY_RETRY_MS = 3_000;
const BUSY_RETRY_MAX = 1200; // about an hour of turn: a command waits, it is not dropped

export async function sendRetryingWhileBusy(
  send: () => Promise<void>,
  opts: { delayMs?: number; maxAttempts?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<void> {
  const delayMs = opts.delayMs ?? BUSY_RETRY_MS;
  const maxAttempts = opts.maxAttempts ?? BUSY_RETRY_MAX;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  for (let attempt = 1; ; attempt++) {
    try {
      await send();
      return;
    } catch (err) {
      // intentional: a busy refusal is retried below; every other error is rethrown here
      if (!isBusyRefusal(err) || attempt >= maxAttempts) throw err;
      await sleep(delayMs);
    }
  }
}
